import { ReactNode, useEffect, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { ArrowDownAZ, ArrowUpZA, Pencil, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  borderPrimary,
  greenTextColor,
  outlinedActionButtonClass as actionButtonClass,
  iconBgAmber,
  iconBgBlue,
  iconBgGreen,
  iconBgRed,
  textBlue,
  lightBlueBG,
  roundedLG,
} from '../../../styling/stylingHelper';
import { getCategoryDisplayName } from '../../orderhistory/categoryUtils';
import { currencyFormatter, hasAnyInvoiceLogo } from '../../../services/helperService';
import { Platform } from '../../../types/Platforms';
import { CountReviewProduct } from '../../../services/inventory/inventoryService';
import { useInventoryViewStore as useInventoryStore } from '../../onboarding/practice/inventoryPractice';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { CustomIngredient, fetchCustomIngredientById } from '../../../services/customIngredients/customIngredientsService';
import { baseProductKey } from '../../../utils/inventoryProductKey';
import { CostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import type { ProductSetupMatch } from '../costPrice/new/CostPrice/ProductSetupModal';
import { Subrecipe } from '../../../services/subrecipes/subrecipesService';
import { computeReferencedSubrecipeIds } from '../../../utils/subrecipeUtils';
import { inventoryKey } from '../../../utils/reports/consumptionReport';
import type { VarianceConfidence, VarianceStatus } from '../../../utils/reports/inventoryVariance';
import { ColumnFilterConfig, TableComponentProps } from '../../ui/TableComponent';
import { SupplierLogo } from '../../ui/SupplierLogo';

const IconLink = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_304_16197" style={{maskType:'luminance'}} maskUnits="userSpaceOnUse" x="1" y="1" width="18" height="18">
      <path d="M18 18V2H2V18H18Z" fill="white" stroke="white" strokeWidth="2"/>
    </mask>
    <g mask="url(#mask0_304_16197)">
      <path d="M10.4912 8.27068C10.7424 8.41375 10.9779 8.5934 11.1923 8.80773C12.4798 10.0953 12.5151 12.1475 10.8953 13.7674L8.70148 15.9612C7.08164 17.581 5.02936 17.5457 3.74182 16.2582C2.45429 14.9706 2.41901 12.9184 4.03882 11.2985L5.63599 9.69481" stroke="#0E8965" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9.50929 11.7292C9.2581 11.5862 9.02257 11.4066 8.80821 11.1922C7.52068 9.90463 7.48543 7.85239 9.10524 6.23258L11.299 4.03879C12.9189 2.41895 14.9711 2.45423 16.2587 3.74176C17.5462 5.02933 17.5815 7.08157 15.9617 8.70139L14.3645 10.3051" stroke="#0E8965" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
  </svg>
);

const IconSetup = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.35938 12.969H9.49338M6.35938 9.05151H12.6274" stroke="#0E8965" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M5.96909 3.17529C4.75012 3.21186 4.02336 3.34754 3.52048 3.85089C2.83203 4.53997 2.83203 5.64904 2.83203 7.86718L2.83203 12.9647C2.83203 15.1828 2.83203 16.2919 3.52048 16.981C4.20892 17.67 5.31696 17.67 7.53303 17.67L11.4505 17.67C13.6666 17.67 14.7746 17.67 15.4631 16.981C16.1515 16.2919 16.1515 15.1828 16.1515 12.9647V7.86718C16.1515 5.64904 16.1515 4.53997 15.4631 3.85089C14.9602 3.34753 14.2334 3.21186 13.0145 3.17529" stroke="#0E8965" strokeWidth="1.2"/>
    <path d="M5.9668 3.37113C5.9668 2.61387 6.58067 2 7.33792 2H11.6472C12.4044 2 13.0183 2.61387 13.0183 3.37113C13.0183 4.12838 12.4044 4.74225 11.6472 4.74225H7.33792C6.58067 4.74225 5.9668 4.12838 5.9668 3.37113Z" stroke="#0E8965" strokeWidth="1.2" strokeLinejoin="round"/>
  </svg>
);

const IconSetPrice = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.16602 8.33325H10.8327" stroke="#0E8965" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.16602 11.6667H10.8327" stroke="#0E8965" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15.834 14.673C14.8918 16.367 13.1773 17.5 11.2186 17.5C8.24476 17.5 5.83398 14.8883 5.83398 11.6667V8.33333C5.83398 5.11167 8.24476 2.5 11.2186 2.5C13.1773 2.5 14.8918 3.63299 15.834 5.32701" stroke="#0E8965" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

/** Columns type for a CountReviewProduct table (mirrors TableComponent's internal ColumnFor). */
export type CountReviewColumns = TableComponentProps<CountReviewProduct>['columns'];

/** Format a quantity number compactly (integers plain, otherwise up to 2 decimals). */
export function fmtQuantity(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * Shared status-badge classes. `badgeBaseClass` is the pill base (used with an inline
 * tint style for colored badges); `grayBadgeClass` is the neutral/gray variant. Reused by
 * the Count Review status badges below and the Leakage tab's status/confidence/flag badges
 * so both stay byte-identical.
 */
export const badgeBaseClass = cn(smallTextSize, semiBoldText, 'px-2 py-0.5 rounded-md whitespace-nowrap');
export const grayBadgeClass = cn(badgeBaseClass, 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300');

/**
 * Tints for the LEAKAGE (inventory variance) status / confidence badges — distinct from
 * `renderStatusBadges` above, which covers Count Review's not-linked/no-price badges.
 * `null` means "render with `grayBadgeClass`". Both the Leakage tab's table and the
 * product drawer read these, so a row keeps its colour when the user opens it.
 */
export const STATUS_TINT: Record<VarianceStatus, { bg: string; color: string } | null> = {
  ok: { bg: iconBgGreen, color: '#079455' },
  review: { bg: iconBgAmber, color: '#B54708' },
  highRisk: { bg: iconBgRed, color: '#D92D20' },
  overCounted: { bg: iconBgBlue, color: '#2E90FA' },
  lowConfidence: null,
};

export const CONFIDENCE_TINT: Record<VarianceConfidence, { bg: string; color: string } | null> = {
  high: { bg: iconBgGreen, color: '#079455' },
  medium: { bg: iconBgAmber, color: '#B54708' },
  low: null,
};

/** Supplier cell: supplier text only, else a dash. */
export function renderSupplier(supplier: { platform: string | null; name: string | null }): ReactNode {
  const { platform, name } = supplier;
  if (!platform) return name ? <span className="truncate">{name}</span> : <span>—</span>;
  const isAnyInvoice = platform.toLowerCase() === 'any_invoice';
  if (isAnyInvoice && (!name || !hasAnyInvoiceLogo(name))) {
    return name ? (
      <span className="truncate" title={name}>{name}</span>
    ) : (
      <span>—</span>
    );
  }
  return (
    <SupplierLogo
      platform={platform as Platform}
      unknownSupplierName={name ?? undefined}
      className="mx-auto"
      alt={platform}
    />
  );
}

/**
 * Status badges (not linked / no price), or a dash. The "below min" badge was removed:
 * the minimum is a cross-section total, so per-row (per-section) flags were misleading —
 * below-minimum products live in their own count-review tab at group level.
 */
export function renderStatusBadges(t: TFunction, row: CountReviewProduct): ReactNode {
  const badges: ReactNode[] = [];
  if (!row.linked)
    badges.push(
      <span key="nl" className={grayBadgeClass}>
        {t('inventory.badgeNotLinked')}
      </span>
    );
  if (row.missingPrice)
    badges.push(
      <span key="np" className={badgeBaseClass} style={{ backgroundColor: iconBgAmber, color: '#B54708' }}>
        {t('inventory.badgeNoPrice')}
      </span>
    );
  // Configured but never counted — the row has no quantity or value behind it.
  if (row.neverCounted)
    badges.push(
      <span key="nc" className={grayBadgeClass}>
        {t('inventory.badgeNeverCounted')}
      </span>
    );
  return badges.length ? <div className="flex items-center justify-start gap-1 flex-wrap">{badges}</div> : <span>—</span>;
}

/**
 * Display name for a live product category. Mirrors ValueByCategoryCard's productCatLabel:
 * the server-resolved 'custom'/'subrecipe'/'uncategorized' buckets get friendly labels;
 * anything else is the fine enum display name. Null → dash. The three sentinel buckets are
 * not enum members, so without a case here `getCategoryDisplayName` falls through and the
 * raw English word renders — in the Dutch UI too.
 */
export function productCategoryLabel(t: TFunction, key: string | null, mainCategory: string | null): string {
  if (!key) return '—';
  if (key === 'custom') return t('inventory.customBucket');
  if (key === 'subrecipe') return t('inventory.subrecipeBucket');
  if (key === 'uncategorized') return t('inventory.uncategorizedBucket');
  return getCategoryDisplayName(key, mainCategory ?? undefined) ?? key;
}

/** Per-std unit price cell: "€42.00 / L", or a dash. */
function renderStdUnitPrice(v: { value: number; unit: string } | null): ReactNode {
  if (!v) return <span>—</span>;
  return <span>{`${currencyFormatter.format(v.value)} / ${v.unit}`}</span>;
}

/**
 * Frozen €/std-display-unit (€/kg, €/L, €/pcs), derived from the item's frozen
 * pricePerStdUnit (which is per ml/g/piece). Never uses live prices.
 */
export function frozenStdUnitPrice(row: CountReviewProduct): { value: number; unit: string } | null {
  if (row.pricePerStdUnit == null || row.pricePerStdUnit === 0) return null;
  if (row.stdUnit === 'ml') return { value: row.pricePerStdUnit * 1000, unit: 'L' };
  if (row.stdUnit === 'g') return { value: row.pricePerStdUnit * 1000, unit: 'kg' };
  return { value: row.pricePerStdUnit, unit: 'pcs' };
}

/** The value a supplier cell filters on (raw platform id, else the supplier text). */
export function supplierFilterValue(row: CountReviewProduct): string {
  return row.supplier.platform ?? row.supplier.name ?? '';
}

/** Distinct supplier filter options as {id, name}, capitalised like the order tables. */
export function buildSupplierFilterOptions(products: CountReviewProduct[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const p of products) {
    const id = supplierFilterValue(p);
    if (!id || seen.has(id)) continue;
    seen.set(id, id.charAt(0).toUpperCase() + id.slice(1));
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The display value an inventory-category cell filters on. */
export function invCategoryFilterValue(t: TFunction, row: CountReviewProduct): string {
  return row.inventoryCategoryName ?? t('inventory.uncategorizedBucket');
}

/** Distinct inventory-category filter options (display strings, sorted). */
export function buildInvCategoryFilterOptions(t: TFunction, products: CountReviewProduct[]): string[] {
  return Array.from(new Set(products.map((p) => invCategoryFilterValue(t, p)))).sort((a, b) => a.localeCompare(b));
}

/** Distinct product-category filter options (display strings, sorted). */
export function buildProductCategoryFilterOptions(t: TFunction, products: CountReviewProduct[]): string[] {
  return Array.from(new Set(products.map((p) => productCategoryLabel(t, p.productCategory, p.mainCategory))))
    .filter((label) => label !== '—')
    .sort((a, b) => a.localeCompare(b));
}

export interface ProductColumnFilters {
  suppliers: string[];
  invCategories: string[];
  prodCategories?: string[];
}

/** Apply the column filter selections (empty selection = no filter on that column). */
export function applyProductColumnFilters(
  t: TFunction,
  products: CountReviewProduct[],
  f: ProductColumnFilters
): CountReviewProduct[] {
  return products.filter(
    (p) =>
      (f.suppliers.length === 0 || f.suppliers.includes(supplierFilterValue(p))) &&
      (f.invCategories.length === 0 || f.invCategories.includes(invCategoryFilterValue(t, p))) &&
      (!f.prodCategories ||
        f.prodCategories.length === 0 ||
        f.prodCategories.includes(productCategoryLabel(t, p.productCategory, p.mainCategory)))
  );
}

/**
 * Which pair of quantity/price columns a product table shows.
 * - `counts`   → the raw counted number **with its count unit** (editable) + € per count unit.
 * - `quantity` → the standardised quantity (kg/L/pcs, read-only) + € per std unit.
 * Undefined keeps the legacy layout (quantity + std unit price, plus a Count column when
 * `countEdit` is set) for tables that don't offer the toggle.
 */
export type ProductCountMode = 'counts' | 'quantity';

export interface ProductColumnOpts {
  sortable?: boolean;
  /** When set, the supplier column gets a filter dropdown instead of sorting. */
  supplierFilter?: ColumnFilterConfig;
  /** When set, the inventory-category column gets a filter dropdown instead of sorting. */
  invCategoryFilter?: ColumnFilterConfig;
  /** When set, the product-category column (expanded set) gets a filter dropdown instead of sorting. */
  productCategoryFilter?: ColumnFilterConfig;
  /** Product-name alphabetical sort: an inline A/Z icon in the header (instead of the ↑↓ arrows). */
  productNameSort?: { active: 'asc' | 'desc' | null; onToggle: () => void };
  /**
   * When set, the "Count" column is inline-editable. Needs the restaurant to scope the
   * correction call.
   */
  countEdit?: { restaurantId: string };
  /** Optional `data-onboarding` id on the inline count control for a given row. */
  countOnboardingId?: (row: CountReviewProduct) => string | undefined;
  /**
   * When set, the "Product" name is inline-editable. Needs the restaurant to scope the
   * correction call.
   */
  nameEdit?: { restaurantId: string };
  /** Counts ⇄ Quantity toggle (see ProductCountMode). Undefined = legacy layout. */
  countMode?: ProductCountMode;
}

/**
 * Inline-editable product-name cell. Renames the product ONLY inside the count this row
 * belongs to (`renameCountProduct` → `POST /counts/:countId/items/rename`): the frozen
 * `productName` is a display snapshot, so nothing about the counted quantity, unit, price or
 * value moves. Deliberately separate from the /inventory/edit rename, which sets the name
 * FUTURE counts are taken under and leaves saved counts alone. Enter or blur commits,
 * Escape cancels.
 */
function EditableProductNameCell({ t, row, restaurantId }: { t: TFunction; row: CountReviewProduct; restaurantId: string }) {
  const renameCountProduct = useInventoryStore((s) => s.renameCountProduct);
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  // Escape blurs the input to leave a single commit path (onBlur); this flag tells that
  // handler the blur was a cancel, not a save.
  const cancelled = useRef(false);

  const commit = async (raw: string) => {
    // Leave edit mode first: the store refetches the whole review on success, so the row
    // object is replaced under us.
    setDraft(null);
    const next = raw.trim();
    if (!next || next === row.productName) return;
    setIsSaving(true);
    setFailed(false);
    try {
      await renameCountProduct(row.countId, restaurantId, row.productKey, next);
    } catch (err) {
      console.error('Failed to rename the product in the inventory count', err);
      setFailed(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (draft !== null) {
    return (
      <input
        type="text"
        autoFocus
        aria-label={t('inventory.editProductNameAria')}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            cancelled.current = true;
            e.currentTarget.blur();
          }
        }}
        onBlur={(e) => {
          if (cancelled.current) {
            cancelled.current = false;
            setDraft(null);
            return;
          }
          void commit(e.target.value);
        }}
        className={cn(
          'w-full min-w-[160px] h-8 px-2 rounded-lg border outline-none bg-white dark:bg-[#14344C]',
          borderPrimary,
          primaryText,
          smallTextSize,
          semiBoldText
        )}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setDraft(row.productName)}
      disabled={isSaving}
      title={failed ? t('inventory.renameCountItemFailed') : t('inventory.editProductNameAria')}
      className={cn(
        smallTextSize,
        semiBoldText,
        'text-left px-2 py-1 rounded-lg border border-transparent transition-colors',
        'hover:border-[#D0D5DD] dark:hover:border-[#2D4F68]',
        failed ? 'text-[#D92D20] dark:text-textRed-dark' : primaryText,
        isSaving ? 'opacity-50' : ''
      )}
    >
      {row.productName}
    </button>
  );
}

/**
 * Inline-editable raw-count cell: the number staff actually entered in the Cloud Count app,
 * shown WITH the unit they counted in (stuks / kg / fles …) so it is obvious what the number
 * means. Click it to correct a miscount — the save goes through the sanctioned
 * quantity-correction path (`updateCountQuantities` → `PUT /counts/:countId/quantities`),
 * which keeps the item's count unit and frozen price and recomputes `countInStdUnit` /
 * value / total_value server-side. Enter or blur commits, Escape cancels. The unit itself is
 * never editable here — it is what staff physically counted in.
 */
function EditableCountCell({
  t,
  row,
  restaurantId,
  onboardingId,
}: {
  t: TFunction;
  row: CountReviewProduct;
  restaurantId: string;
  onboardingId?: string;
}) {
  const updateCountQuantities = useInventoryStore((s) => s.updateCountQuantities);
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  // Escape blurs the input to leave a single commit path (onBlur); this flag tells that
  // handler the blur was a cancel, not a save.
  const cancelled = useRef(false);

  useEffect(() => {
    if (!onboardingId) return;
    const onOpen = () => setDraft(String(row.count));
    window.addEventListener('onboarding:inventory-open-count-edit', onOpen);
    return () => window.removeEventListener('onboarding:inventory-open-count-edit', onOpen);
  }, [onboardingId, row.count]);

  const commit = async (raw: string) => {
    // Leave edit mode first: the store refetches the whole review on success, so the row
    // object is replaced under us.
    setDraft(null);
    const next = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(next) || next < 0 || next === row.count) return;
    setIsSaving(true);
    setFailed(false);
    try {
      await updateCountQuantities(row.countId, restaurantId, [{ productKey: row.productKey, count: next }]);
    } catch (err) {
      console.error('Failed to update inventory count quantity', err);
      setFailed(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (draft !== null) {
    return (
      <div className="flex items-center gap-1.5" data-onboarding={onboardingId ? `${onboardingId} inventory-section-count-input` : undefined}>
        <input
          type="number"
          min="0"
          step="any"
          autoFocus
          aria-label={t('inventory.editCountAria')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              cancelled.current = true;
              e.currentTarget.blur();
            }
          }}
          onBlur={(e) => {
            if (cancelled.current) {
              cancelled.current = false;
              setDraft(null);
              return;
            }
            void commit(e.target.value);
          }}
          className={cn(
            'w-[68px] h-10 sm:h-8 px-2 rounded-lg border outline-none bg-white dark:bg-[#14344C]',
            borderPrimary,
            primaryText,
            smallTextSize,
            semiBoldText
          )}
        />
        <span className={cn(secondaryText, smallTextSize, 'whitespace-nowrap')}>{row.countUnit}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-onboarding={onboardingId}
      onClick={() => setDraft(String(row.count))}
      disabled={isSaving}
      title={failed ? t('inventory.updateCountFailed') : t('inventory.editCountAria')}
      className={cn(
        smallTextSize,
        semiBoldText,
        'px-2 py-1 rounded-lg border border-transparent transition-colors whitespace-nowrap',
        'hover:border-[#D0D5DD] dark:hover:border-[#2D4F68]',
        failed ? 'text-[#D92D20] dark:text-textRed-dark' : primaryText,
        isSaving ? 'opacity-50' : ''
      )}
    >
      {fmtQuantity(row.count)} <span className={cn(secondaryText, 'font-normal')}>{row.countUnit}</span>
    </button>
  );
}

/**
 * Base product columns shared by the count-review attention table (P2) and the section
 * detail products table (P3). Pass `sortable` to enable TableComponent's column sorting;
 * the supplier/category columns take filter dropdowns instead (OrderHistoryProductsPage style).
 */
export function buildBaseProductColumns(t: TFunction, opts: ProductColumnOpts = {}): CountReviewColumns {
  const s = opts.sortable ? { sortable: true } : {};
  const nameSort = opts.productNameSort;
  // Counts mode swaps the standardised quantity + €/std-unit pair for the raw count (with its
  // unit) + €/count-unit. Without a mode, the legacy layout stands: quantity + €/std unit,
  // plus a Count column whenever inline editing is enabled.
  const showCount = opts.countMode ? opts.countMode === 'counts' : Boolean(opts.countEdit);
  const showQuantity = opts.countMode ? opts.countMode === 'quantity' : true;
  const countUnitPricing = opts.countMode === 'counts';
  return [
    {
      key: 'productName',
      // Alphabetical A/Z icon inline in the label (the built-in ↑↓ button wraps below
      // the label in the first column, and A/Z reads better for a name sort).
      label: nameSort ? (
        <span className="inline-flex items-center">
          {t('inventory.product')}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              nameSort.onToggle();
            }}
            className="ml-1.5 hover:opacity-70 transition-opacity"
            aria-label="Sort by product name"
          >
            {nameSort.active === 'desc' ? (
              <ArrowUpZA className="w-3.5 h-3.5 text-[#0E8965] dark:text-darkGreenColor-dark" />
            ) : (
              <ArrowDownAZ
                className={cn(
                  'w-3.5 h-3.5',
                  nameSort.active === 'asc'
                    ? 'text-[#0E8965] dark:text-darkGreenColor-dark'
                    : 'text-[#9CA3AF] dark:text-textTertiary-dark'
                )}
              />
            )}
          </button>
        </span>
      ) : (
        t('inventory.product')
      ),
      render: (_v, row) => {
        // Never-counted config rows have no saved count line to rename.
        if (!opts.nameEdit || !row.countId) {
          return <span className={cn(primaryText, smallTextSize, semiBoldText)}>{row.productName}</span>;
        }
        return <EditableProductNameCell t={t} row={row} restaurantId={opts.nameEdit.restaurantId} />;
      },
    },
    {
      key: 'supplier',
      label: t('inventory.colSupplier'),
      // 'category' columnType caps the column at 120px with horizontal scroll inside
      // (OrderHistoryProductsPage categories column); our render still supplies the cell.
      columnType: 'category',
      ...(opts.supplierFilter ? { filter: opts.supplierFilter } : { ...s, sortKey: 'supplier' }),
      render: (_v, row) => renderSupplier(row.supplier),
    },
    {
      key: 'inventoryCategoryName',
      label: t('inventory.colInvCategory'),
      columnType: 'category',
      ...(opts.invCategoryFilter ? { filter: opts.invCategoryFilter } : { ...s, sortKey: 'inventoryCategoryName' }),
      render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{row.inventoryCategoryName ?? '—'}</span>,
    },
    // The counted number in the unit staff counted in, editable in place when `countEdit` is set.
    ...(showCount
      ? ([
          {
            key: 'count',
            label: t('inventory.count'),
            ...s,
            sortKey: 'count',
            render: (_v: number, row: CountReviewProduct) => {
              // Never-counted config rows have no saved count to correct.
              if (!row.countId) return <span>—</span>;
              if (!opts.countEdit) {
                return (
                  <span className={cn(primaryText, smallTextSize, semiBoldText, 'whitespace-nowrap')}>
                    {fmtQuantity(row.count)} <span className={cn(secondaryText, 'font-normal')}>{row.countUnit}</span>
                  </span>
                );
              }
              return (
                <EditableCountCell
                  t={t}
                  row={row}
                  restaurantId={opts.countEdit.restaurantId}
                  onboardingId={opts.countOnboardingId?.(row)}
                />
              );
            },
          },
        ] as CountReviewColumns)
      : []),
    ...(showQuantity
      ? ([
          {
            key: 'quantityDisplay',
            label: t('inventory.colQuantity'),
            ...s,
            sortKey: 'quantityDisplay',
            render: (_v: { value: number; unit: string }, row: CountReviewProduct) => (
              <span className={cn(textBlue, smallTextSize, semiBoldText, lightBlueBG, roundedLG, 'px-2 py-1 whitespace-nowrap')}>
                {`${fmtQuantity(row.quantityDisplay.value)} ${row.quantityDisplay.unit}`}
              </span>
            ),
          },
        ] as CountReviewColumns)
      : []),
    // Counts mode prices ONE counted unit (€ per fles/kg/stuk); quantity mode keeps the
    // per-std-unit price (€/kg, €/L, €/pcs). Both are frozen, never live.
    countUnitPricing
      ? {
          key: 'unitPrice',
          label: t('inventory.colPrice'),
          ...s,
          sortKey: 'countUnitPrice',
          render: (_v, row) => (
            <span className={cn(secondaryText, smallTextSize, 'whitespace-nowrap')}>
              {row.unitPrice != null ? `${currencyFormatter.format(row.unitPrice)} / ${row.countUnit}` : '—'}
            </span>
          ),
        }
      : {
          key: 'unitPrice',
          label: t('inventory.unitPrice'),
          ...s,
          sortKey: 'unitPrice',
          render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{renderStdUnitPrice(frozenStdUnitPrice(row))}</span>,
        },
    {
      key: 'value',
      label: t('inventory.value'),
      ...s,
      sortKey: 'value',
      render: (_v, row) => <span className={cn(primaryText, smallTextSize, semiBoldText)}>{currencyFormatter.format(row.value)}</span>,
    },
  ];
}

/**
 * Inventory keys of every order product referenced by any cost price analysis
 * ingredient or subrecipe ingredient — INCLUDING their ingredient alternatives.
 * A counted product that IS linked to order history but missing from this set can
 * never receive expected usage from the leakage needed-index (it would always read
 * 0) — the "Not setup" attention tab filters `linked && !keys.has(productKey)`
 * against this set. Alternatives are indexed too so a product linked only as an
 * alternative (which the leakage walk still pools demand onto) also counts as set up.
 *
 * Counted SUBRECIPES join the set under their own `subrecipe:<id>` key when a cost-price
 * recipe reaches them, directly or through nesting — so the same not-setup predicates work
 * on prep rows unchanged, and an orphan prep (no recipe uses it) reads "Not setup".
 */
export function buildCostPriceInventoryKeys(
  costPriceList: CostPriceAnalysis[],
  subrecipesById: Map<string, Subrecipe>
): Set<string> {
  const keys = new Set<string>();
  const add = (platform?: string, articleId?: string, productName?: string) => {
    // Subrecipe / nested-cost-price reference rows carry no platform → skipped.
    if (!platform) return;
    const key = inventoryKey(platform, articleId, productName);
    if (key) keys.add(key);
  };
  const addWithAlternatives = (ing: { platform?: string; articleId?: string; productName?: string; alternatives?: { platform: string; articleId: string; productName: string }[] }) => {
    add(ing.platform, ing.articleId, ing.productName);
    for (const alt of ing.alternatives ?? []) add(alt.platform, alt.articleId, alt.productName);
  };
  for (const cpa of costPriceList) {
    for (const ing of cpa.ingredients) addWithAlternatives(ing);
  }
  for (const sub of Array.from(subrecipesById.values())) {
    for (const ing of sub.ingredients) addWithAlternatives(ing);
  }
  computeReferencedSubrecipeIds(costPriceList, subrecipesById).forEach((id) => keys.add(`subrecipe:${id}`));
  return keys;
}

/**
 * A trailing "Actions" column for a count-review table. The caller supplies the per-row cell
 * (e.g. a re-link button gated on link status) — kept separate from the base columns so tables
 * that don't need actions stay unchanged.
 */
export function actionColumn(
  t: TFunction,
  render: (row: CountReviewProduct) => ReactNode
): CountReviewColumns[number] {
  // `key` must be a keyof CountReviewProduct — reuse the assignment-id field (the cell ignores
  // its value and renders the caller-supplied action instead).
  return {
    key: 'inventorySectionProductId',
    label: t('inventory.colActions'),
    render: (_v: string | null, row: CountReviewProduct) => render(row),
  };
}

/** Destructive variant of the action button (delete a counted line). */
const dangerActionButtonClass = cn(
  'inline-flex items-center gap-1 px-[7px] py-[5px] text-[14px] font-medium leading-[20px] rounded-md border',
  'border-[#FDA29B] dark:border-[#912018] text-[#D92D20] dark:text-[#F97066] dark:bg-transparent'
);

/** Icon-only action button for the mobile attention cards. */
const mobileActionButtonClass = cn(
  'inline-flex items-center justify-center w-8 h-8 rounded-lg border shrink-0',
  borderPrimary,
  greenTextColor,
  'dark:bg-transparent'
);

/**
 * A row is re-linkable through its assignment, or — when the assignment behind a counted
 * row is gone (product removed from the section, or re-linked to another key after the
 * count was saved) — through its section + frozen product key. Without either there is
 * nothing to point at a target, so no Link action is offered.
 */
export function canRelink(row: CountReviewProduct): boolean {
  return Boolean(row.inventorySectionProductId || (row.sectionId && row.productKey));
}

/**
 * What the setup flow should treat a count-review row as. A counted prep names its
 * subrecipe directly — there is no purchased product behind it — so it resolves by id
 * (variant suffix stripped). Every other row keys its order product by the inventory key
 * it was counted under.
 */
export function setupMatchForReviewRow(row: CountReviewProduct): ProductSetupMatch {
  const base = baseProductKey(row.productKey);
  if (row.source === 'subrecipe' && base.startsWith('subrecipe:')) {
    return { kind: 'subrecipe', subrecipeId: base.slice('subrecipe:'.length) };
  }
  return { kind: 'inventory', inventoryKey: row.productKey };
}

/**
 * Whether "Set price" is worth offering. A subrecipe is priced from its own ingredients and
 * is excluded from the durable, restaurant-wide inventory price, so the only write left for
 * a prep is the correction of an already-frozen count. A configured-but-never-counted prep
 * row has neither — offering the action there would open a modal that can save nothing.
 */
export function canSetPrice(row: CountReviewProduct): boolean {
  if (!row.missingPrice) return false;
  return row.source !== 'subrecipe' || Boolean(row.countId);
}

/**
 * The custom ingredient behind a custom-sourced row — the Edit action's target. Its key is
 * `custom:<id>` (a variant row carries a `__vN` suffix, so the base key is taken first).
 * Served from the cost-price store when that already holds THIS restaurant's ingredients,
 * otherwise fetched by id: the inventory pages never load the full custom-ingredient list,
 * and one row's edit is no reason to pull it in. Null when the row is not custom or the
 * ingredient can't be loaded.
 */
export async function loadCustomIngredientForRow(
  row: CountReviewProduct,
  restaurantId: string
): Promise<CustomIngredient | null> {
  if (row.source !== 'custom') return null;
  const base = baseProductKey(row.productKey);
  const id = base.startsWith('custom:') ? base.slice('custom:'.length) : '';
  if (!id) return null;
  const store = useCostPriceStore.getState();
  const cached = store.isCustomIngredientsLoadedFor(restaurantId) ? store.customIngredientById(id) : undefined;
  if (cached) return cached;
  try {
    return await fetchCustomIngredientById(id);
  } catch (err) {
    console.error('Failed to load the custom ingredient behind an inventory row', err);
    return null;
  }
}

/**
 * Combined Actions cell: "Link" on not-linked rows (when `onRelink` given), "Edit" on
 * custom-ingredient rows (when `onEditCustom` given), "Set price" on missing-price rows
 * (when `onSetPrice` given), "Setup" on not-setup rows (when `onSetup` given — the CALLER
 * gates it on `linked && !costPriceKeys.has(productKey)`, this cell shows it whenever the
 * handler is present) and "Delete" on rows backed by a saved count (when `onDelete` given).
 * Neither → a dash.
 */
export function renderProductActions(
  t: TFunction,
  row: CountReviewProduct,
  handlers: {
    onRelink?: (row: CountReviewProduct) => void;
    onEditCustom?: (row: CountReviewProduct) => void;
    onSetPrice?: (row: CountReviewProduct) => void;
    onSetup?: (row: CountReviewProduct) => void;
    onDelete?: (row: CountReviewProduct) => void;
    linkOnboardingId?: string;
    setupOnboardingId?: string;
  }
): ReactNode {
  const buttons: ReactNode[] = [];
  // Offered on every assignment: unlinked rows need a first link, linked ones can be
  // re-pointed at a different order product (wrong match) — same endpoint, different label.
  if (handlers.onRelink && canRelink(row)) {
    buttons.push(
      <button
        key="link"
        type="button"
        onClick={() => handlers.onRelink!(row)}
        className={actionButtonClass}
        data-onboarding={!row.linked ? handlers.linkOnboardingId : undefined}
      >
        <IconLink />
        {t(row.linked ? 'inventory.changeLinkAction' : 'inventory.linkAction')}
      </button>
    );
  }
  // Custom ingredients are edited in place (name, supplier, price, pack size). Gated on the
  // row's SOURCE, not on its link status — an order-sourced row can be unlinked too, and it
  // has no custom ingredient behind it to edit.
  if (handlers.onEditCustom && row.source === 'custom') {
    buttons.push(
      <button key="editCustom" type="button" onClick={() => handlers.onEditCustom!(row)} className={actionButtonClass}>
        <Pencil className="w-4 h-4" />
        {t('inventory.edit')}
      </button>
    );
  }
  if (handlers.onSetPrice && canSetPrice(row)) {
    buttons.push(
      <button key="price" type="button" onClick={() => handlers.onSetPrice!(row)} className={actionButtonClass}>
        <IconSetPrice />
        {t('inventory.setPriceAction')}
      </button>
    );
  }
  if (handlers.onSetup) {
    buttons.push(
      <button
        key="setup"
        type="button"
        onClick={() => handlers.onSetup!(row)}
        className={actionButtonClass}
        data-onboarding={handlers.setupOnboardingId}
      >
        <IconSetup />
        {t('inventory.setupAction')}
      </button>
    );
  }
  // Only lines that really sit in a saved count can be removed from it.
  if (handlers.onDelete && row.countId) {
    buttons.push(
      <button
        key="delete"
        type="button"
        onClick={() => handlers.onDelete!(row)}
        className={dangerActionButtonClass}
        aria-label={t('inventory.deleteCountItemAction')}
      >
        <Trash2 className="w-4 h-4" />
        {t('inventory.deleteCountItemAction')}
      </button>
    );
  }
  if (buttons.length === 0) return <span>—</span>;
  return <div className="flex flex-row items-center justify-start gap-1.5">{buttons}</div>;
}

/** The single tab-driven mobile action (Link / Set price / Setup), or null. */
function mobilePrimaryAction(
  t: TFunction,
  row: CountReviewProduct,
  handlers: {
    onRelink?: (row: CountReviewProduct) => void;
    onSetPrice?: (row: CountReviewProduct) => void;
    onSetup?: (row: CountReviewProduct) => void;
    linkOnboardingId?: string;
    setupOnboardingId?: string;
  }
): ReactNode {
  if (handlers.onRelink && !row.linked && canRelink(row)) {
    return (
      <button
        type="button"
        onClick={() => handlers.onRelink!(row)}
        className={mobileActionButtonClass}
        aria-label={t('inventory.linkAction')}
        data-onboarding={handlers.linkOnboardingId}
      >
        <IconLink />
      </button>
    );
  }
  if (handlers.onSetPrice && canSetPrice(row)) {
    return (
      <button
        type="button"
        onClick={() => handlers.onSetPrice!(row)}
        className={mobileActionButtonClass}
        aria-label={t('inventory.setPriceAction')}
      >
        <IconSetPrice />
      </button>
    );
  }
  if (handlers.onSetup) {
    return (
      <button
        type="button"
        onClick={() => handlers.onSetup!(row)}
        className={mobileActionButtonClass}
        aria-label={t('inventory.setupAction')}
        data-onboarding={handlers.setupOnboardingId}
      >
        <IconSetup />
      </button>
    );
  }
  // Re-link an already-linked row: last, so a missing price / setup still wins the single slot.
  if (handlers.onRelink && canRelink(row)) {
    return (
      <button
        type="button"
        onClick={() => handlers.onRelink!(row)}
        className={mobileActionButtonClass}
        aria-label={t('inventory.changeLinkAction')}
      >
        <IconLink />
      </button>
    );
  }
  return null;
}

/**
 * Mobile attention card action: the icon-only twin of `renderProductActions`. Custom rows
 * additionally get "Edit" — it sits next to the tab action rather than replacing it, since
 * a custom row's Link action stays meaningful. Returns null when no action applies.
 */
export function renderMobileProductAction(
  t: TFunction,
  row: CountReviewProduct,
  handlers: {
    onRelink?: (row: CountReviewProduct) => void;
    onEditCustom?: (row: CountReviewProduct) => void;
    onSetPrice?: (row: CountReviewProduct) => void;
    onSetup?: (row: CountReviewProduct) => void;
    linkOnboardingId?: string;
    setupOnboardingId?: string;
  }
): ReactNode {
  const primary = mobilePrimaryAction(t, row, handlers);
  const edit =
    handlers.onEditCustom && row.source === 'custom' ? (
      <button
        type="button"
        onClick={() => handlers.onEditCustom!(row)}
        className={mobileActionButtonClass}
        aria-label={t('inventory.edit')}
      >
        <Pencil className="w-4 h-4" />
      </button>
    ) : null;
  if (!edit) return primary;
  if (!primary) return edit;
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      {edit}
      {primary}
    </div>
  );
}

/**
 * Full column set for the section detail "See all" modal: same as base columns (sortable).
 */
export function buildExpandedProductColumns(t: TFunction, opts: ProductColumnOpts = {}): CountReviewColumns {
  return buildBaseProductColumns(t, { sortable: true, ...opts });
}

/** Sort key → comparable primitive, shared by the section detail products table + modal. */
export function productSortValue(row: CountReviewProduct, sortKey: string): string | number {
  switch (sortKey) {
    case 'productName':
      return row.productName.toLowerCase();
    case 'supplier':
      return (row.supplier.platform ?? row.supplier.name ?? '').toLowerCase();
    case 'inventoryCategoryName':
      return (row.inventoryCategoryName ?? '').toLowerCase();
    case 'count':
      // The Count column shows the raw counted number, so it sorts on that.
      return row.count;
    case 'quantityDisplay':
      return row.quantityDisplay.value;
    case 'unitPrice':
      return row.pricePerStdUnit ?? -Infinity;
    case 'countUnitPrice':
      // Counts mode: the frozen € per counted unit (null on rows counted as 0 → sort last).
      return row.unitPrice ?? -Infinity;
    case 'value':
      return row.value;
    case 'productCategory':
      return (row.productCategory ?? '').toLowerCase();
    default:
      return 0;
  }
}

/** Apply a (column, direction) sort to a product list. Returns a new array; null column = unsorted. */
export function sortProducts(
  products: CountReviewProduct[],
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc' | null
): CountReviewProduct[] {
  if (!sortColumn || !sortDirection) return products;
  const dir = sortDirection === 'asc' ? 1 : -1;
  return [...products].sort((a, b) => {
    const av = productSortValue(a, sortColumn);
    const bv = productSortValue(b, sortColumn);
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}
